import React, { useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';

// Reads the chosen file into a data URL (documents are stored inline on the
// record, not uploaded separately) and enforces a size cap first.
//
//   <FileInput label="Baptism Certificate" fileName={form.baptismCert}
//     onFile={(name, dataUrl) => setForm({...form, baptismCert: name, baptismCertUrl: dataUrl})}
//     onError={notifyError} />
export interface FileInputProps {
  label?: ReactNode;
  /** Name of the already-uploaded file, shown as confirmation. */
  fileName?: string;
  accept?: string;
  maxBytes?: number;
  /** Receives the filename and a base64 data URL. */
  onFile?: (name: string, dataUrl: string) => void;
  onError?: (message: string) => void;
  /** Small note under the field, e.g. the size limit. */
  hint?: ReactNode;
  fullWidth?: boolean;
  containerStyle?: CSSProperties;
  isDarkMode?: boolean;
}

const FileInput = ({
  label,
  fileName,
  accept = '.pdf, image/*',
  maxBytes = 1_500_000,
  onFile,
  onError,
  hint,
  fullWidth = false,
  containerStyle,
  isDarkMode
}: FileInputProps) => {
  const s = useStyles(isDarkMode);
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxBytes) {
      onError?.(`File too large (Max ${Math.round(maxBytes / 100000) / 10}MB).`);
      // Clear it so the same file can be re-picked after shrinking.
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      // readAsDataURL always produces a string; the DOM type is a union
      // shared with readAsArrayBuffer.
      if (typeof reader.result === 'string') onFile?.(file.name, reader.result);
      else onError?.('Could not read the selected file.');
    };
    reader.onerror = () => onError?.('Could not read the selected file.');
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ ...s.inputGroup, ...(fullWidth ? s.fullWidth : {}), ...containerStyle }}>
      {label && <label style={s.label}>{label}</label>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{
          padding: '8px', border: `1px dashed ${s.t.borderStrong}`,
          borderRadius: s.t.radius.sm, fontSize: s.t.fontSize.base,
          backgroundColor: s.t.bgInput, color: s.t.textMain, cursor: 'pointer'
        }}
      />
      {hint && (
        <span style={{ fontSize: s.t.fontSize.sm, color: s.t.textFaint, marginTop: '4px' }}>
          {hint}
        </span>
      )}
      {fileName && (
        <span style={{ fontSize: s.t.fontSize.sm, color: s.t.income, marginTop: '4px', fontWeight: 'bold' }}>
          Uploaded: {fileName}
        </span>
      )}
    </div>
  );
};

export default FileInput;
